/**
 * errorCopy — human-readable error message mapping (issue #429).
 *
 * Centralizes the "what should we show the user when something
 * went wrong" decision so screens never leak raw
 * `e.message` strings like `AuthRetryableFetchError: Failed to
 * fetch` to clinicians. Each helper inspects the shape of the
 * error it was given and returns the user-friendly copy for
 * the matching category.
 *
 * Categories:
 *   - network     — fetch failure / offline / 5xx
 *   - auth        — 401 / token expired / session required
 *   - validation  — field-level error from server (first field wins)
 *   - rateLimit   — 429 / "slow down"
 *   - generic     — fallback when no specific category matched
 *
 * Each helper is total: it never throws, never returns an
 * empty string, and never returns a raw exception class name.
 * Components can call them without try/catch and trust the
 * returned string is ready to render.
 */

const NETWORK_FALLBACK =
  "We couldn't reach the server. Check your connection and try again.";
const AUTH_FALLBACK =
  "Your session expired. Sign in again to continue.";
const RATE_LIMIT_FALLBACK =
  "You're going a bit fast — try again in a moment.";
const GENERIC_FALLBACK =
  "Something went wrong on our end. Try again in a moment.";

interface ErrorLike {
  message?: unknown;
  status?: unknown;
  code?: unknown;
  name?: unknown;
  fields?: unknown;
}

/**
 * Safely read a string field from an unknown error-ish value.
 * Returns `undefined` for non-objects / wrong types so the
 * callers can fall back without nested try/catch.
 */
function pickErrorLike(input: unknown): ErrorLike {
  if (input == null || typeof input !== 'object') return {};
  return input as ErrorLike;
}

function readNumber(input: unknown): number | undefined {
  if (typeof input === 'number' && Number.isFinite(input)) return input;
  return undefined;
}

function readString(input: unknown): string | undefined {
  if (typeof input === 'string' && input.length > 0) return input;
  return undefined;
}

/**
 * Inspect the error message + name + status + code fields and
 * return a category discriminator. Used by the per-category
 * helpers to decide whether the error matches.
 */
export type ErrorCategory = 'network' | 'auth' | 'validation' | 'rateLimit' | 'generic';

export function classifyError(input: unknown): ErrorCategory {
  const e = pickErrorLike(input);

  const status = readNumber(e.status);
  if (status === 401 || status === 403) return 'auth';
  if (status === 429) return 'rateLimit';
  if (status !== undefined && status >= 500) return 'network';
  if (status !== undefined && status >= 400) return 'validation';

  const message = (readString(e.message) ?? '').toLowerCase();
  const name = (readString(e.name) ?? '').toLowerCase();
  const code = (readString(e.code) ?? '').toLowerCase();
  const blob = `${message} ${name} ${code}`;

  if (blob.includes('failed to fetch') || blob.includes('networkerror') || blob.includes('network request failed')) {
    return 'network';
  }
  if (blob.includes('jwt') || blob.includes('authsessionmissing') || blob.includes('refresh_token') || blob.includes('invalid_grant')) {
    return 'auth';
  }
  if (blob.includes('429') || blob.includes('rate limit') || blob.includes('too many')) {
    return 'rateLimit';
  }
  if (blob.includes('required') || blob.includes('invalid') || blob.includes('validation')) {
    return 'validation';
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'network';
  }
  return 'generic';
}

/**
 * networkError — returns the user-friendly copy for a
 * reach-the-server failure: offline, DNS, timeout, 5xx,
 * `TypeError: Failed to fetch`.
 *
 * The `input` parameter is part of the uniform per-category
 * helper shape (so callers can swap between `networkError(e)`,
 * `authError(e)`, etc. without changing the call site). The
 * raw input is currently not branched on because the user copy
 * is identical across the network-failure shapes we recognize;
 * preserved for forward compat (e.g. a future "API quota
 * exhausted" sub-message).
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function networkError(_input: unknown): string {
  return NETWORK_FALLBACK;
}

/**
 * authError — returns the user-friendly copy for a stale or
 * missing session. 401 / 403 / JWT-expired map here.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function authError(_input: unknown): string {
  return AUTH_FALLBACK;
}

/**
 * rateLimitError — returns the user-friendly copy for a 429.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function rateLimitError(_input: unknown): string {
  return RATE_LIMIT_FALLBACK;
}

/**
 * validationError — returns the user-friendly copy for a
 * field-level server error. If the error carries a `fields`
 * array (`{message: string}[]`), the first message wins
 * because server-side ordering usually reflects priority
 * (most important constraint first).
 *
 * If `fields` is missing or empty, returns a generic
 * "Check the highlighted fields" prompt so we never leak a
 * raw error.toString().
 */
export function validationError(input: unknown): string {
  const e = pickErrorLike(input);
  const raw = e.fields;
  if (Array.isArray(raw)) {
    for (const field of raw) {
      if (field == null || typeof field !== 'object') continue;
      const msg = (field as Record<string, unknown>).message;
      const s = readString(msg);
      if (s) return s;
    }
  }
  return 'Check the highlighted fields and try again.';
}

/**
 * humanError — drop-in replacement for `e.message` rendering.
 * Classifies the error and returns the matching friendly
 * copy, with a graceful fallback for anything we don't
 * recognize.
 *
 * Use this instead of `e.message` in screen-level error
 * states so we never leak raw `Error: AuthRetryableFetchError:`
 * strings to the user.
 */
export function humanError(input: unknown): string {
  const category = classifyError(input);
  switch (category) {
    case 'network':
      return networkError(input);
    case 'auth':
      return authError(input);
    case 'validation':
      return validationError(input);
    case 'rateLimit':
      return rateLimitError(input);
    case 'generic':
    default:
      return GENERIC_FALLBACK;
  }
}
